"use client";

// ── A result opens its code (`EX-SCB05`, Wave 24 decision 6) ──────────────────
// Pressing a result on the standings opens this sheet: every attempt that row
// made on that problem, the chosen attempt's verdict, and its program in
// `ProgramSourceView` (coloured, `EX-SCB04`). The canvas's Popup board is the
// layout: the letter and its balloon in the head, the attempts on the left,
// the verdict and the code on the right, the reader's access in the foot.
//
// **Who may open what is three reads, never one.** A moderator reads the
// console's list and detail (the grader's view: which test failed); a
// contestant reads their own list under `count_only` and their own program
// (`GET .../submissions/:id/source`), so they learn how many tests passed and
// never which; a signed-in reader of a published round reads the field list
// narrowed to the row and the field program read (ADR-0082). The server gates
// each read on its own; `resultAccessFor` only decides which result is a
// button, so a page never offers a program its read would refuse.

import { useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ArrowUpRight, Clock, Lock } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ProgramSourceView } from "@/components/code/ProgramSourceView";
import { SubmissionResultDetail } from "@/features/judge/components/workspace/SubmissionResult";
import { verdictTone } from "@/features/judge/lib/format";
import type { JudgeVerdict, StudentTestResult } from "@/features/quiz/coding-task-api";
import { formatRuntimeLanguageName } from "@/features/quiz/lib/coding-task-format";
import {
  useContestFieldSubmissionSource,
  useContestFieldSubmissions,
  useMyContestSubmissionSource,
  useMyContestSubmissions,
  useOrganizerSubmission,
  useOrganizerSubmissions,
} from "../hooks";
import { formatContestMinute } from "../lib/format";
import { balloonHex, rowLabel } from "../lib/standings-summary";
import type { StandingsTheme } from "../lib/standings-theme";
import type { ContestStandingsResponse, StandingsCell, StandingsRow } from "../types";

/** Which reads serve a row: the console's, the reader's own, or a published round's field. */
export type ResultAccess = "moderator" | "own" | "field";

/** What the board knows about the reader, passed in by the page that shows it. */
export type ResultReader = {
  canModerate: boolean;
  signedIn: boolean;
  /** The contest page's `sourceOpen`: the round is published and its list is open. */
  sourceOpen: boolean;
};

/**
 * Whether a row's results open for this reader, and through which reads:
 * a moderator any row, a contestant their own row (`you.entryId`, which is
 * the team's row in a team contest), and a signed-in reader every row once
 * the organizer published the round. `null` keeps the result a plain picture.
 */
export function resultAccessFor(reader: ResultReader, isYou: boolean): ResultAccess | null {
  if (reader.canModerate) return "moderator";
  if (isYou && reader.signedIn) return "own";
  if (reader.sourceOpen && reader.signedIn) return "field";
  return null;
}

/** One attempt, whichever list it came from. Oldest first once normalised. */
type Attempt = {
  submissionId: string;
  createdAt: string;
  status: "queued" | "running" | "completed";
  verdict: JudgeVerdict | null;
  timeMs: number | null;
  memoryKb: number | null;
  languageKey: string | null;
  /** Who submitted it, on a team row the console reads (a member's name). */
  who: string | null;
  /** `count_only`'s pass count, the contestant's own list only. */
  hidden: { passedCount: number; totalCount: number } | null;
  compileOutput: string | null;
};

/** Attempts on one problem are read in one page; past it, the foot points to Submissions. */
const ATTEMPT_LIMIT = 100;

/** The short verdicts a contest hall reads; the full name is the attempt's title. */
export const SHORT_VERDICT: Readonly<Record<JudgeVerdict, string>> = {
  accepted: "AC",
  wrong_answer: "WA",
  time_limit_exceeded: "TLE",
  memory_limit_exceeded: "MLE",
  runtime_error: "RE",
  compilation_error: "CE",
  presentation_error: "PE",
  internal_error: "IE",
  skipped: "SK",
};

/** A verdict's pill, per theme, on the board's own scoreboard tokens (`docs/03` §3). */
const VERDICT_TONE: Readonly<Record<StandingsTheme, Readonly<Record<ReturnType<typeof verdictTone> | "judging", string>>>> = {
  dark: {
    success: "bg-scoreboard-solved-night text-scoreboard-solved-night-ink",
    error: "bg-scoreboard-wrong-night text-scoreboard-wrong-night-ink",
    warning: "bg-scoreboard-judging-night text-scoreboard-judging-night-ink",
    judging: "bg-scoreboard-judging-night text-scoreboard-judging-night-ink",
    neutral: "bg-white/10 text-neutral-300",
  },
  light: {
    success: "bg-scoreboard-solved-day text-white",
    error: "bg-scoreboard-wrong-day text-scoreboard-wrong-day-ink",
    warning: "bg-scoreboard-judging-day text-scoreboard-judging-day-ink",
    judging: "bg-scoreboard-judging-day text-scoreboard-judging-day-ink",
    neutral: "bg-neutral-100 text-neutral-600",
  },
};

/** An attempt's pill tone; the console's `SubmissionSheet` reads the same pills. */
export function toneOf(attempt: Pick<Attempt, "status" | "verdict">, theme: StandingsTheme): string {
  return VERDICT_TONE[theme][attempt.status === "completed" ? verdictTone(attempt.verdict) : "judging"];
}

/** The first test that failed in the grader's view; a test never run because an earlier one failed is not it. */
export function firstFailedTest(tests: readonly StudentTestResult[] | null | undefined): number | null {
  const failed = [...(tests ?? [])]
    .sort((a, b) => a.idx - b.idx)
    .find((test) => test.verdict !== "accepted" && test.verdict !== "skipped");
  return failed ? failed.idx : null;
}

function oldestFirst(attempts: Attempt[]): Attempt[] {
  return [...attempts].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

export function ResultSheet({
  slug,
  contestId,
  locale,
  theme,
  access,
  row,
  cell,
  problem,
  problemTitle,
  contest,
  onClose,
  onOpenSubmissions,
}: {
  slug: string;
  contestId: string;
  locale: string;
  theme: StandingsTheme;
  access: ResultAccess;
  row: StandingsRow;
  cell: StandingsCell;
  problem: ContestStandingsResponse["problems"][number] | undefined;
  problemTitle?: string;
  contest: ContestStandingsResponse["contest"];
  onClose: () => void;
  /** The contest page's Submissions tab, on this letter; absent where there is no such tab. */
  onOpenSubmissions?: (target: { letter: string; scope: "mine" | "field" }) => void;
}) {
  const t = useTranslations("contest.standings");
  const tPage = useTranslations("contest.page");
  const tJudge = useTranslations("judge");
  const [chosen, setChosen] = useState<string | null>(null);
  const dark = theme === "dark";
  const letter = cell.letter;
  const who = row.entryType === "team" ? { teamId: row.entryId } : { userId: row.entryId };

  const own = useMyContestSubmissions(slug, access === "own", { letter, limit: ATTEMPT_LIMIT });
  const field = useContestFieldSubmissions(slug, { letter, limit: ATTEMPT_LIMIT, ...who }, access === "field");
  const moderated = useOrganizerSubmissions(contestId, { letter, limit: ATTEMPT_LIMIT, ...who }, access === "moderator");
  const list = access === "own" ? own : access === "field" ? field : moderated;

  const attempts: Attempt[] = oldestFirst(
    access === "own"
      ? (own.data?.items ?? []).map((item) => ({
          submissionId: item.submissionId,
          createdAt: item.createdAt,
          status: item.status,
          verdict: item.verdict ?? null,
          timeMs: item.timeMs ?? null,
          memoryKb: item.memoryKb ?? null,
          languageKey: item.languageKey ?? null,
          who: null,
          hidden: item.hidden ?? null,
          compileOutput: item.compileOutput ?? null,
        }))
      : access === "field"
        ? (field.data?.items ?? []).map((item) => ({
            submissionId: item.submissionId,
            createdAt: item.createdAt,
            status: item.status,
            verdict: item.verdict ?? null,
            timeMs: null,
            memoryKb: null,
            languageKey: item.languageKey,
            who: row.entryType === "team" ? item.name : null,
            hidden: null,
            compileOutput: null,
          }))
        : (moderated.data?.items ?? []).map((item) => ({
            submissionId: item.submissionId,
            createdAt: item.createdAt,
            status: item.status,
            verdict: item.verdict,
            timeMs: item.timeMs,
            memoryKb: item.memoryKb,
            languageKey: item.languageKey,
            who: row.entryType === "team" ? item.name : null,
            hidden: null,
            compileOutput: null,
          })),
  );
  const total = list.data?.total ?? attempts.length;
  const selected = attempts.find((attempt) => attempt.submissionId === chosen) ?? attempts[attempts.length - 1] ?? null;
  const selectedId = selected?.submissionId ?? null;

  const ownSource = useMyContestSubmissionSource(slug, access === "own" ? selectedId : null);
  const fieldSource = useContestFieldSubmissionSource(slug, access === "field" ? selectedId : null);
  const detail = useOrganizerSubmission(contestId, access === "moderator" ? selectedId : null);
  const source = access === "own" ? ownSource : access === "field" ? fieldSource : detail;
  const program = source.data ? { code: source.data.sourceCode, languageKey: source.data.languageKey, languageVersion: source.data.languageVersion } : null;

  const startMs = contest.startAt ? Date.parse(contest.startAt) : NaN;
  const minuteOf = (iso: string) =>
    Number.isFinite(startMs) ? formatContestMinute((Date.parse(iso) - startMs) / 60_000) : null;
  // "After the freeze" is the moderator's note while the freeze still holds:
  // the contestants' board does not show those attempts yet.
  const freezeMs = contest.freezeAt ? Date.parse(contest.freezeAt) : NaN;
  const freezeHolds = access === "moderator" && Number.isFinite(freezeMs) && contest.phase !== "unfrozen";

  const verdictName = (attempt: Attempt) =>
    attempt.status !== "completed" ? t("sheetJudging") : attempt.verdict ? tJudge(`verdict.${attempt.verdict}`) : t("sheetJudging");

  const detailLine = (attempt: Attempt): string => {
    if (attempt.status !== "completed" || !attempt.verdict) return t("sheetJudging");
    const name = verdictName(attempt);
    if (access === "own") {
      if (!attempt.hidden || attempt.verdict === "compilation_error") return name;
      return attempt.verdict === "accepted"
        ? t("sheetAllPassed", { total: attempt.hidden.totalCount })
        : t("sheetPassedOf", { passed: attempt.hidden.passedCount, total: attempt.hidden.totalCount });
    }
    if (access === "moderator" && detail.data && detail.data.submissionId === attempt.submissionId) {
      const tests = detail.data.testResults ?? [];
      if (attempt.verdict === "accepted") return tests.length > 0 ? t("sheetAllPassed", { total: tests.length }) : name;
      const failed = firstFailedTest(tests);
      return failed !== null ? t("sheetOnTest", { verdict: name, test: failed }) : name;
    }
    return name;
  };

  const metaLine = (attempt: Attempt): string => {
    const timeMs = access === "moderator" && detail.data?.submissionId === attempt.submissionId ? (detail.data.timeMs ?? attempt.timeMs) : attempt.timeMs;
    const memoryKb =
      access === "moderator" && detail.data?.submissionId === attempt.submissionId ? (detail.data.memoryKb ?? attempt.memoryKb) : attempt.memoryKb;
    const at = minuteOf(attempt.createdAt);
    return [
      timeMs !== null ? t("sheetTime", { seconds: Math.round(timeMs / 10) / 100 }) : null,
      memoryKb !== null ? t("sheetMemory", { mb: Math.round(memoryKb / 102.4) / 10 }) : null,
      attempt.languageKey ? formatRuntimeLanguageName(attempt.languageKey) : null,
      at ? t("sheetAt", { at }) : null,
    ]
      .filter((part): part is string => part !== null)
      .join(" · ");
  };

  const compileOutput =
    selected?.verdict === "compilation_error"
      ? access === "moderator"
        ? detail.data?.submissionId === selected.submissionId
          ? (detail.data.compileOutput ?? null)
          : null
        : selected.compileOutput
      : null;

  const name = rowLabel(row);
  const hex = balloonHex(problem?.balloonColor);
  const title = problemTitle ? t("sheetTitle", { letter, title: problemTitle }) : letter;
  // A team row read by one member lists that member's attempts only; the cell
  // counts the whole team's, so the foot says why the two differ.
  const teamNote = access === "own" && row.entryType === "team" && list.isSuccess && attempts.length < cell.attempts + cell.pending;
  const note =
    access === "moderator" ? t("sheetNoteModerator") : access === "own" ? t("sheetNoteOwn") : t("sheetNoteField");
  const muted = dark ? "text-neutral-400" : "text-neutral-500";

  const leading = (
    <span
      className={cn(
        "relative grid h-[38px] w-[38px] place-items-center rounded-[11px] text-base font-bold text-white",
        dark ? "bg-neutral-800" : "bg-neutral-900",
      )}
    >
      {letter}
      {hex ? (
        <>
          <span
            aria-hidden
            className={cn("absolute -right-1 -top-1 h-[13px] w-[13px] rounded-full ring-2", dark ? "ring-neutral-900" : "ring-white")}
            style={{ backgroundColor: hex }}
          />
          <span className="sr-only">{t("balloonLabel", { name: problem?.balloonColor?.name ?? "" })}</span>
        </>
      ) : null}
    </span>
  );

  const openSubmissions =
    access === "moderator" ? (
      <Link
        href={`/${locale}/dashboard/contests/${contestId}/submissions?letter=${encodeURIComponent(letter)}`}
        className={buttonVariants({ variant: dark ? "ghostDark" : "outline", size: "sm" })}
      >
        {t("sheetOpenSubmissions")}
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
      </Link>
    ) : onOpenSubmissions ? (
      <Button
        type="button"
        variant={dark ? "ghostDark" : "outline"}
        size="sm"
        onClick={() => {
          onOpenSubmissions({ letter, scope: access === "own" ? "mine" : "field" });
          onClose();
        }}
      >
        {t("sheetOpenSubmissions")}
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
      </Button>
    ) : null;

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={
        row.rank === null
          ? t("sheetSubUnofficial", { who: name, count: total })
          : t("sheetSub", { who: name, rank: row.rank, count: total })
      }
      closeLabel={t("sheetClose")}
      tone={dark ? "dark" : "light"}
      size="sheet"
      leading={leading}
    >
      <div data-result-sheet={access} data-sheet-theme={theme} className="flex min-h-0 flex-1 flex-col sm:flex-row">
        <nav
          aria-label={t("sheetAttempts")}
          className={cn(
            "flex shrink-0 gap-1.5 overflow-x-auto border-b p-3 sm:w-[236px] sm:flex-col sm:overflow-y-auto sm:overflow-x-visible sm:border-b-0 sm:border-r",
            dark ? "border-white/10 bg-neutral-950/60" : "border-neutral-200 bg-neutral-50",
          )}
        >
          <p className={cn("hidden px-1.5 pb-1 text-[10.5px] font-bold uppercase tracking-[0.09em] sm:block", muted)}>
            {t("sheetAttempts")}
          </p>
          {list.isPending ? (
            <>
              <Skeleton className="h-10 w-28 shrink-0 rounded-xl sm:w-full" />
              <Skeleton className="h-10 w-28 shrink-0 rounded-xl sm:w-full" />
            </>
          ) : list.isError ? (
            <p className={cn("px-1.5 text-sm", muted)}>{t("sheetFailed")}</p>
          ) : attempts.length === 0 ? (
            <p className={cn("px-1.5 text-sm", muted)}>{t("sheetEmpty")}</p>
          ) : (
            attempts.map((attempt, index) => {
              const on = attempt.submissionId === selectedId;
              const at = minuteOf(attempt.createdAt);
              const afterFreeze = freezeHolds && Date.parse(attempt.createdAt) >= freezeMs;
              return (
                <Button
                  key={attempt.submissionId}
                  type="button"
                  variant={null}
                  size={null}
                  aria-pressed={on}
                  title={verdictName(attempt)}
                  onClick={() => setChosen(attempt.submissionId)}
                  className={cn(
                    "grid shrink-0 grid-cols-[auto_1fr_auto] items-center gap-x-2.5 gap-y-0.5 rounded-xl px-[11px] py-2.5 text-left text-[13px] font-medium transition-colors duration-150",
                    dark ? "text-white hover:bg-white/10" : "text-neutral-900 hover:bg-white",
                    on && (dark ? "bg-white/10 ring-1 ring-white/15" : "bg-white shadow-sm ring-1 ring-neutral-200"),
                  )}
                >
                  {/* Lighter than `muted` on dark: the chosen row's lifted ground would take it under 4.5:1. */}
                  <span className={cn("font-bold", dark ? "text-neutral-300" : "text-neutral-500")}>{`#${index + 1}`}</span>
                  <span className="font-bold tabular-nums">{at ?? ""}</span>
                  <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-bold", toneOf(attempt, theme))}>
                    {attempt.status !== "completed" ? <Clock className="h-3 w-3" aria-hidden /> : null}
                    {attempt.status === "completed" && attempt.verdict ? SHORT_VERDICT[attempt.verdict] : null}
                  </span>
                  {attempt.who ? <span className={cn("col-span-3 truncate text-[11px]", muted)}>{t("sheetBy", { name: attempt.who })}</span> : null}
                  {afterFreeze ? (
                    <span className={cn("col-span-3 text-[11px] font-semibold", dark ? "text-scoreboard-frozen-night-ink" : "text-scoreboard-frozen-day-ink")}>
                      {t("sheetAfterFreeze")}
                    </span>
                  ) : null}
                </Button>
              );
            })
          )}
        </nav>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-4 sm:px-[18px]">
          {selected ? (
            <div className="flex flex-wrap items-center gap-3.5">
              <span className={cn("whitespace-nowrap rounded-full px-3.5 py-[7px] text-sm font-bold", toneOf(selected, theme))}>
                {verdictName(selected)}
              </span>
              <div className="min-w-0">
                <p data-sheet-detail className={cn("text-[14.5px] font-bold", dark ? "text-white" : "text-neutral-900")}>
                  {detailLine(selected)}
                </p>
                <p className={cn("mt-0.5 text-[12.5px]", muted)}>{metaLine(selected)}</p>
              </div>
            </div>
          ) : null}
          {compileOutput ? <SubmissionResultDetail result={{ compileOutput }} /> : null}
          {selected ? (
            source.isError ? (
              <p className={cn("text-sm", muted)}>{t("sheetProgramFailed")}</p>
            ) : program ? (
              <ProgramSourceView
                source={program.code}
                languageKey={program.languageKey}
                languageName={program.languageKey ? formatRuntimeLanguageName(program.languageKey) : null}
                languageVersion={program.languageVersion}
                labels={{
                  lines: tPage("programLines", { count: program.code.split("\n").length }),
                  wrap: tPage("programWrap"),
                  copy: tPage("programCopy"),
                  copied: tPage("programCopied"),
                }}
              />
            ) : (
              <Skeleton className="h-60 w-full rounded-xl" />
            )
          ) : null}
        </div>
      </div>

      <footer
        className={cn(
          "flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-t px-4 py-3 sm:px-5",
          dark ? "border-white/10" : "border-neutral-200",
        )}
      >
        <div className={cn("min-w-0 flex-1 space-y-0.5 text-[12.5px]", muted)}>
          <p>
            <Lock className="mr-1.5 inline h-[13px] w-[13px] align-[-2px]" aria-hidden />
            {note}
          </p>
          {teamNote ? <p data-sheet-team-note>{t("sheetNoteTeam")}</p> : null}
          {total > attempts.length ? <p>{t("sheetMore", { shown: attempts.length, total })}</p> : null}
        </div>
        {openSubmissions}
      </footer>
    </Dialog>
  );
}
